import React, { MouseEvent, useContext } from 'react'

import NumbersOnlyInput from './NumbersOnlyInput'

export interface IProps {
  elementClass: string
  outerClass: string
  inputClass?: string
  id?: string
  name?: string
  unit?: string
  min?: number
  max?: number
  value: number
  defaultValue?: number
  disabled?: boolean
  onChange?: (value: number) => void
  onMinus: (e: MouseEvent) => void
  onPlus: (e: MouseEvent) => void
  onChangeComplete?: (value: number) => void
  format?: (value: string) => void
  userTracking?: any
  trackElement?: string
  rf?: string
  rfPlus?: string
  rfMinus?: string
  ariaLabel?: string
}

/**
 * Plus-Minus number input
 *
 *
 * @param elementClass (string, required) - Main component css class.
 * @param outerClass (string, required) - Css class for input outer container
 * @param inputClass (string, optional) - Css input class
 * @param id (string, required) - Input id
 * @param name (string, required) - Input name
 * @param value (number, required) - Contolled input value
 * @param defaultValue (number, optional) - Default value.
 * @param unit (string, optional) - Unit in input
 * @param min (number, optional) - Min input value
 * @param max (number, optional) - Max input value
 * @param disabled (boolean, optional) - Enable / disable input
 * @param onChange (void: number, optional) - On change handler
 * @param onMinus (void: MouseEvent, required) - On minus click handler
 * @param onPlus (void: MouseEvent, required) - On plus click handler
 * @param onChangeComplete (void, optional) - On complete change input change handler (on input blur, enter)
 * @param format (void: string, optional) - Formatter function. Accept string value and return (formatted) string
 * @param userTracking (string, optional) - User tracking service
 * @param trackElement (string, optional) - User tracking track element id
 * @param rf (string, optional) - Optional tracking id fro automated tests
 * @param rfPlus (string, optional) - Optional tracking id fro automated tests
 * @param rfMinus (string, optional) - Optional tracking id fro automated tests
 * @param ariaLabel (string, optional) - Optional aria label for plus / minus buttons
 *
 */
const PlusMinusInput = (props: IProps) => {
  const { trackElement, max, min, value, onPlus, onMinus, userTracking: propsUserTracking } = props
  const userTracking = propsUserTracking

  const handlePlus = (e: MouseEvent) => {
    userTracking.onClick(trackElement, 'plus')
    if (max !== undefined && max !== null) {
      if (max > value) {
        onPlus(e)
      }
    } else {
      onPlus(e)
    }
  }

  const handleMinus = (e: MouseEvent) => {
    userTracking.onClick(trackElement, 'minus')
    if (min !== undefined && min !== null) {
      if (min < value) {
        onMinus(e)
      }
    } else {
      onMinus(e)
    }
  }

  return (
    <div className={props.elementClass}>
      <button
        className='csas-calc-controls csas-calc-controls--minus'
        disabled={props.disabled}
        type='button'
        data-testid={props.rfMinus}
        onClick={handleMinus}
        aria-label={props.ariaLabel + ' - minus'}
      ></button>
      <div className={props.outerClass}>
        <NumbersOnlyInput
          name={props.name}
          id={props.id}
          value={props.value}
          unit={props.unit}
          min={props.min}
          max={props.max}
          inputClass={props.inputClass}
          disabled={props.disabled}
          onChange={props.onChange}
          onChangeComplete={props.onChangeComplete}
          format={props.format}
          userTracking={props.userTracking}
          trackElement={props.trackElement}
          rf={props.rf}
        />
      </div>
      <button
        className='csas-calc-controls csas-calc-controls--plus'
        disabled={props.disabled}
        type='button'
        data-testid={props.rfPlus}
        onClick={handlePlus}
        aria-label={props.ariaLabel + ' - plus'}
      ></button>
    </div>
  )
}

export default PlusMinusInput
